import { Spacing } from '@persona-id/design-tokens';
import { LoaderIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { useMemo } from 'react';
import ReactModal from 'react-modal';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import { NAVBAR_COLLAPSED_WIDTH, NAVBAR_EXPANDED_WIDTH } from 'dashboard/components/Navbar';
import { RootState } from 'dashboard/redux/store';
import { Label } from 'design-system/components/Text';
import { Elevation } from 'design-system/variables/effect';
import { rotate } from 'design-system/variables/keyframes';

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: auto;
  height: ${Spacing[9]};
  padding: ${Spacing[4]};
  gap: ${Spacing[3]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  box-shadow:
    0 0 0 3px '#ffffff',
    ${Elevation.High};
`;

const LoaderSpinner = styled(LoaderIcon)`
  height: 22px;
  width: 22px;
  color: ${({ theme }) => theme.color.primary.brand700};
  animation: ${rotate} linear 1s infinite;
`;

const LoaderLabel = styled(Label).attrs(() => ({ size: 'lg', weight: 'bold' }))`
  font-weight: 400;
`;

const loadingModalStyles: ReactModal.Styles = {
  overlay: {
    left: `${NAVBAR_EXPANDED_WIDTH}px`,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  content: {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    marginLeft: 'unset',
    marginRight: 'unset',
  },
};

interface LoadingModalProps {
  title?: string;
  isLoading: boolean;
}

const LoadingModal = ({ title = 'Loading...', isLoading }: LoadingModalProps) => {
  const navbarCollapsed = useSelector<RootState, boolean>((state) => state.navbar.navbarCollapsed);
  const currentStyles = useMemo<ReactModal.Styles>(
    () =>
      navbarCollapsed
        ? produce(loadingModalStyles, (draftStyles) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            draftStyles.overlay.left = NAVBAR_COLLAPSED_WIDTH;
          })
        : loadingModalStyles,
    [navbarCollapsed],
  );
  return (
    <ModalWrapper
      styleOverride={currentStyles}
      isOpen={isLoading}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      <LoaderContainer>
        <LoaderSpinner aria-label='Loading Spinner' />
        <LoaderLabel>{title}</LoaderLabel>
      </LoaderContainer>
    </ModalWrapper>
  );
};

export default LoadingModal;
